import React, { useState } from "react";

import { v4 as uuidv4 } from "uuid";
import { Bot, Loader2, Plus, Table, Trash } from "lucide-react";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../components/ui/accordion";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";

import { useToast } from "../../components/hooks/use-toast";
import { useAiModel } from "../../components/hooks/useAiModel";

import RiskRatingDisplay from "../../components/Risk Matrix/RiskRatingDisplay";
import RiskMatrixSelectionModal from "../../components/Risk Matrix/RiskMatrixSelectionModal";

import functionsService from "../../services/functionsService";

const HazardMitigations = ({ project, riskMatrix, hazard, setHazard }) => {
  const [isGeneratingMitigations, setIsGeneratingMitigations] = useState(false);
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);

  const [selectedModel] = useAiModel();
  const { toast } = useToast();

  const handleChange = (name, value) => {
    setHazard((prev) => {
      // Update nested property
      if (name.includes(".")) {
        const lhsName = name.split(".")[0];
        const rhsName = name.split(".")[1];

        // Unmitigated risk
        if (lhsName === "unmitigatedRisk") {
          return {
            ...prev,
            unmitigatedRisk: {
              ...prev.unmitigatedRisk,
              [rhsName]: value, // Update the nested property
            },
          };
        }

        // Retained risk
        else if (lhsName === "retainedRisk") {
          return {
            ...prev,
            retainedRisk: {
              ...prev.retainedRisk,
              [rhsName]: value, // Update the nested property
            },
          };
        }
      }

      // Update top-level property
      return { ...prev, [name]: value };
    });
  };

  const generateMitigations = async () => {
    setIsGeneratingMitigations(true);

    try {
      const { success, mitigations } =
        await functionsService.generateHazardMitigations(
          project.id,
          hazard.description,
          hazard.type,
          hazard.hazardSource,
          hazard.causes,
          hazard.consequences,
          selectedModel
        );

      if (!success) {
        console.error("Failed to generate mitigations.");

        toast({
          variant: "destructive",
          title: "Failed to generate mitigations.",
        });

        return;
      }

      setHazard((prev) => ({
        ...prev,
        mitigations: [
          ...prev.mitigations,
          ...mitigations
        ],
      }));
    } catch (error) {
      console.error("Failed to generate mitigations.", error);

      toast({
        variant: "destructive",
        title: "Failed to generate mitigations.",
        description: error.message,
      });
    } finally {
      setIsGeneratingMitigations(false);
    }
  };

  const handleMitigationChange = (index, field, value) => {
    setHazard((prev) => {
      const updatedMitigations = [...prev.mitigations];

      updatedMitigations[index] = {
        ...updatedMitigations[index],
        [field]: value,
      };

      return { ...prev, mitigations: updatedMitigations };
    });
  };

  const addMitigation = () => {
    setHazard((prev) => ({
      ...prev,
      mitigations: [
        ...prev.mitigations,
        {
          id: uuidv4(),
          mitigationId: "",
          type: "Elimination",
          description: "",
          owner: "",
          status: "Proposed",
          dueDate: "",
          safetyRequirement: "DOORS LINK",
          evidence: "",
        },
      ],
    }));
  };

  const removeMitigation = (index) => {
    setHazard((prev) => ({
      ...prev,
      mitigations: prev.mitigations.filter((_, i) => i !== index),
    }));
  };

  const handleMatrixCellSelection = (probability, severity) => {
    handleChange("retainedRisk.probability", probability);
    handleChange("retainedRisk.severity", severity);
  };

  return (
    <div className="space-y-4">
      {/* Add Mitigation Button */}
      <div className="flex justify-between items-center">
        <Button type="button" onClick={addMitigation}>
          <Plus size={16} className="mr-2" />
          Add Mitigation
        </Button>

        <Button
          type="button"
          onClick={generateMitigations}
          disabled={
            !hazard.description ||
            !hazard.consequences ||
            isGeneratingMitigations
          }
        >
          {isGeneratingMitigations ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Bot size={16} className="mr-2" />
          )}
          Generate Mitigations
        </Button>
      </div>

      {/* Mitigations Grid */}
      <div className="flex flex-col space-y-4">
        {hazard.mitigations.map((mitigation, index) => (
          <Card key={mitigation.id} className="relative">
            <CardContent className="p-4 bg-gray-100">
              {/* Mitigation ID */}
              <div className="absolute top-2 left-2">
                <span className="font-semibold text-xs text-gray-400">
                  ID:{" "}
                  {mitigation.mitigationId
                    ? mitigation.mitigationId
                    : "Automatic ID"}
                </span>
              </div>

              {/* Top Row - Type and Status */}
              <div className="flex justify-between items-start mb-2 pt-4">
                <div className="w-[45%]">
                  <Label>Type</Label>

                  <Select
                    value={mitigation.type}
                    onValueChange={(value) =>
                      handleMitigationChange(index, "type", value)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>

                    <SelectContent className="z-[10001]">
                      {[
                        "Elimination",
                        "Substitution",
                        "Engineering",
                        "Administrative",
                        "Personal Protective Equipment",
                      ].map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[45%]">
                  <Label>Status</Label>

                  <Select
                    value={mitigation.status}
                    onValueChange={(value) =>
                      handleMitigationChange(index, "status", value)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>

                    <SelectContent className="z-[10001]">
                      {[
                        "Proposed",
                        "Implemented",
                        "Transferred",
                        "Rejected",
                      ].map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Middle - Description */}
              <div className="mb-4">
                <Label>Description</Label>

                <Textarea
                  value={mitigation.description}
                  onChange={(e) =>
                    handleMitigationChange(index, "description", e.target.value)
                  }
                  className="min-h-[80px] resize-none"
                  placeholder="Mitigation description..."
                />
              </div>

              {/* Bottom Row - Owner and Due Date */}
              <div className="flex justify-between items-end mb-4">
                <div className="w-[45%]">
                  <Label>Owner</Label>

                  <Select
                    value={mitigation.owner}
                    onValueChange={(value) =>
                      handleMitigationChange(index, "owner", value)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>

                    <SelectContent className="z-[10001]">
                      {project.projectMetadata?.mitigationOwners?.map(
                        (owner) => (
                          <SelectItem key={owner} value={owner}>
                            {owner}
                          </SelectItem>
                        )
                      ) || []}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[45%]">
                  <Label>Due Date</Label>

                  <Input
                    type="date"
                    value={mitigation.dueDate}
                    onChange={(e) =>
                      handleMitigationChange(index, "dueDate", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Safety Requirement */}
              <div className="mb-4">
                <p className="text-sm text-gray-500 italic">
                  Safety Requirement:{" "}
                  {mitigation.safetyRequirement || "Not specified"}
                </p>
              </div>

              {/* Evidence Accordion */}
              <Accordion type="single" collapsible>
                <AccordionItem value="evidence">
                  <AccordionTrigger>Evidence</AccordionTrigger>

                  <AccordionContent>
                    <div className="py-2">
                      <Input
                        value={mitigation.evidence}
                        onChange={(e) =>
                          handleMitigationChange(
                            index,
                            "evidence",
                            e.target.value
                          )
                        }
                        placeholder="Add evidence link or reference..."
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Remove Button */}
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => removeMitigation(index)}
              >
                <Trash size={8} />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Retained Risk Section */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4">Retained Risk</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Probability</Label>
            <Select
              value={hazard.retainedRisk.probability}
              onValueChange={(value) =>
                handleChange("retainedRisk.probability", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select probability" />
              </SelectTrigger>

              <SelectContent className="z-[10001]">
                {riskMatrix?.probabilities?.map((prob) => (
                  <SelectItem key={prob} value={prob}>
                    {prob}
                  </SelectItem>
                )) || []}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Severity</Label>

            <Select
              value={hazard.retainedRisk.severity}
              onValueChange={(value) =>
                handleChange("retainedRisk.severity", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select severity" />
              </SelectTrigger>

              <SelectContent className="z-[10001]">
                {riskMatrix?.severities?.map((sev) => (
                  <SelectItem key={sev} value={sev}>
                    {sev}
                  </SelectItem>
                )) || []}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-between items-center mt-2">
          <RiskRatingDisplay
            probability={hazard.retainedRisk.probability}
            severity={hazard.retainedRisk.severity}
            riskMatrix={riskMatrix}
          />

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsRiskMatrixModalOpen(true)}
          >
            <Table className="h-4 w-4 mr-2" />
            Select from Matrix
          </Button>
        </div>
      </div>

      {/* Risk Matrix Selection Modal */}
      <RiskMatrixSelectionModal
        isOpen={isRiskMatrixModalOpen}
        setIsOpen={setIsRiskMatrixModalOpen}
        riskMatrix={riskMatrix}
        onSelectCell={handleMatrixCellSelection}
        title="Select Retained Risk Values"
        description="Click on a cell in the risk matrix to select both probability and severity values."
        riskType="retained"
      />
    </div>
  );
};

export default HazardMitigations;
